import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Link from "next/link";
import { useRouter } from "next/router";

import classNames from "classnames";
import PropTypes from "prop-types";

import { useAddToCart, useBuyNow } from "@/api/client/cart";
import { useGetNotificationsData } from "@/api/client/notifications";
import { useRequestProductStock } from "@/api/client/product";
import {
  useAddWishlistData,
  useRemoveWishlistData,
} from "@/api/client/wishlist";
import PrimaryBtn from "@/components/actions/button/primaryBtn";
import TertiaryBtn from "@/components/actions/button/tertiaryBtn";
import {
  DecrementIcon,
  IncrementIcon,
  WishlistActiveIcon,
  WishlistInactiveIcon,
} from "@/components/data-display/icon";
import ProductLabel from "@/components/data-display/label/productLabel";
import { errorToast, successToast } from "@/components/feedback/toast";
import { updateCartItemCount } from "@/store/feature/cartSlice";
import {
  getWishlistState,
  updateWishlistItemCount,
} from "@/store/feature/wishlistSlice";
import { getProductStockText, isAuthorizedUser } from "@/utils/helpers";
import { updateSingleWithWishlistStatus } from "@/utils/helpers/wishlist";

function ProductDetails({ product }) {
  const [quantity, setQuantity] = useState(1);
  const [isWishlist, setIsWishlist] = useState(false);

  const { wishlistItems } = useSelector(getWishlistState);

  const router = useRouter();
  const dispatch = useDispatch();

  const isDecrementDisabled = quantity <= 1;
  const isIncrementDisabled = quantity >= product.current_stock;
  const productCategory = product.categories[product.categories.length - 1];

  useEffect(() => {
    const isProductInWishlist = updateSingleWithWishlistStatus(
      product.slug,
      wishlistItems
    );

    setIsWishlist(isProductInWishlist.is_whitelisted);
  }, [wishlistItems]);

  const { refetch: refetchNotificaitonData } = useGetNotificationsData({
    enabled: false,
  });

  const { mutate: addToCart, isLoading: isAddToCartLoading } = useAddToCart({
    onSuccess: ({ cartData }) => {
      successToast({ message: "Added to cart successfully" });
      dispatch(updateCartItemCount(cartData?.cart?.products?.length));
    },
    onError: (errorData) => {
      errorToast({ message: errorData.message });
    },
  });

  const { mutate: buyNow, isLoading: isBuyNowLoading } = useBuyNow({
    onSuccess: ({ cartData }) => {
      dispatch(updateCartItemCount(cartData?.cart?.products?.length));
      router.push("/checkout");
    },
    onError: (errorData) => {
      errorToast({ message: errorData.message });
    },
  });

  const { mutate: addToWishlist, isLoading: isAddingToWishlist } =
    useAddWishlistData({
      onSuccess: (res) => {
        setIsWishlist(true);
        successToast({ message: res.message });
        dispatch(updateWishlistItemCount(res?.wishlist?.length));
      },
      onError: (errorData) => {
        errorToast({ message: errorData.message });
      },
    });

  const { mutate: removeWishlist, isLoading: isRemovingFromWishlist } =
    useRemoveWishlistData({
      onSuccess: (res) => {
        setIsWishlist(false);
        successToast({ message: res.message });
        refetchNotificaitonData();
      },
      onError: ({ message }) => {
        errorToast({ message });
      },
    });

  const { isLoading: isRequestingProductStock, mutate: requestProductStock } =
    useRequestProductStock({
      onSuccess: () => {
        successToast({
          message: "Your request has been submitted succesfully",
        });
      },
      onError: ({ message }) => {
        errorToast({ message });
      },
    });

  const handleAddToCart = async () => {
    addToCart({
      product: product.slug,
      quantity: quantity,
    });
  };

  const handleWishlist = async () => {
    const isAuthorized = await isAuthorizedUser();
    if (!isAuthorized) {
      return;
    }

    if (!isWishlist) {
      addToWishlist({ product: product.slug });
    } else {
      removeWishlist({ product: product.slug });
    }
  };

  const handleBuyNow = async () => {
    const isAuthorized = await isAuthorizedUser();
    if (!isAuthorized) {
      return;
    }

    buyNow({
      product: product.slug,
      quantity: quantity,
    });
  };

  const handleRequestProductStock = async () => {
    const isAuthorized = await isAuthorizedUser();
    if (!isAuthorized) {
      return;
    }

    requestProductStock({ slug: product.slug });
  };

  return (
    <section>
      {product.sku && (
        <p className="mb-[0.75rem] text-[0.875rem] text-grayDisplay-800">
          Product ID: {product.sku}
        </p>
      )}
      <h1 className="mb-[1.125rem] text-[1.5rem] font-semibold text-grayDisplay-900">
        {product.name}
      </h1>
      <div className="mb-[1.75rem] flex items-center space-x-[2.5rem] text-[0.875rem]">
        {product.brand?.name && (
          <div className="space-x-1">
            <span className="text-grayDisplay-850">Brand</span>
            <Link href={`/brands/${product.brand.slug}`}>
              <span className="text-tertiary-500">{product.brand.name}</span>
            </Link>
          </div>
        )}

        <div className="space-x-1">
          <span className="text-grayDisplay-850">Category</span>
          <Link href={`/category/${productCategory.slug}`}>
            <span className="text-tertiary-500">{productCategory.name}</span>
          </Link>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-[2.25rem]">
        {product.promotions?.length > 0 && (
          <div className="flex gap-[0.5rem]">
            {product.promotions.map((label) => (
              <ProductLabel
                key={label.key}
                label={label.content}
                type={label.key}
              />
            ))}
          </div>
        )}

        <button
          onClick={handleWishlist}
          disabled={isAddingToWishlist || isRemovingFromWishlist}
          className="flex cursor-pointer items-center disabled:cursor-wait"
        >
          {isWishlist ? (
            <WishlistActiveIcon className="mt-1 h-[2.25rem] w-[2.375rem]" />
          ) : (
            <WishlistInactiveIcon className="mt-1 h-[2.25rem] w-[2.375rem]" />
          )}

          <p className="ml-[0.625rem] text-base font-medium lg:text-[1.125rem]">
            {isWishlist ? "Added to Wishlist" : "Add to Wishlist"}
          </p>
        </button>
      </div>

      <div className="my-[1.75rem] border" />
      <div className="mb-[2.25rem] flex items-center space-x-[1.438rem]">
        <p className="text-[1.75rem] font-semibold text-grayDisplay-900">
          ৳{product.has_discount_price ? product.discount_price : product.price}
        </p>
        {product.has_discount_price && (
          <p className="text-[1.125rem] text-grayDisplay-850 line-through">
            ৳{product.price}
          </p>
        )}
      </div>
      <p className="mb-[2.5rem] mt-[2.5rem] text-grayDisplay-400">
        {product.description}
      </p>

      <div className="flex flex-col space-y-[1.188rem] lg:flex-row lg:items-center lg:space-x-[1.563rem] lg:space-y-0">
        <p>Quantity</p>
        <div className="flex items-center space-x-[1.563rem]">
          <div className="flex w-[150px] items-center rounded-[12px]">
            <button
              disabled={isDecrementDisabled}
              onClick={() => setQuantity((preVal) => preVal - 1)}
              className="group flex h-[2.625rem] w-[2.5rem] items-center justify-center rounded-l-[0.5rem] bg-grayDisplay-250 disabled:cursor-not-allowed"
            >
              <DecrementIcon
                className={classNames(
                  {
                    "text-grayDisplay-900 hover:text-primary":
                      !isDecrementDisabled,
                  },
                  "h-[2.125rem] w-[2.125rem] text-grayDisplay-850"
                )}
              />
            </button>
            <div className="flex h-[2.625rem] w-[3.75rem] items-center justify-center border border-grayDisplay-100 font-semibold">
              <p
                className={classNames({
                  "text-grayDisplay-850": product.current_stock < 1,
                })}
              >
                {quantity}
              </p>
            </div>
            <button
              disabled={isIncrementDisabled}
              onClick={() => setQuantity((preVal) => preVal + 1)}
              className="group flex h-[2.625rem] w-[2.5rem] items-center justify-center rounded-r-[0.5rem] bg-grayDisplay-250 disabled:cursor-not-allowed"
            >
              <IncrementIcon
                className={classNames(
                  {
                    "text-grayDisplay-900 hover:text-primary":
                      !isIncrementDisabled,
                  },
                  "h-[2.125rem] w-[2.125rem] text-grayDisplay-850"
                )}
              />
            </button>
          </div>

          <div>{getProductStockText(product?.current_stock)}</div>
        </div>
      </div>

      <div className="mt-[3rem]">
        {product?.current_stock > 0 ? (
          <div className="space-x-[1rem]">
            <PrimaryBtn
              btnText="Buy Now"
              onClick={handleBuyNow}
              isLoading={isBuyNowLoading}
              isDisabled={product.current_stock < 1}
              className="h-[3rem] w-[9.5rem] lg:w-[11.25rem]"
            />

            <TertiaryBtn
              btnText="Add to cart"
              onClick={handleAddToCart}
              isLoading={isAddToCartLoading}
              isDisabled={product.current_stock < 1}
              className="h-[3rem] w-[9.5rem] lg:w-[11.25rem]"
            />
          </div>
        ) : (
          <PrimaryBtn
            btnText="Request Product"
            isLoading={isRequestingProductStock}
            onClick={handleRequestProductStock}
            className="bg-tertiary-300 px-[1.75rem] py-[0.906rem] text-[1rem] font-semibold !text-[#18AA2B] hover:!bg-[#bcf0bb]"
          />
        )}
      </div>
    </section>
  );
}

ProductDetails.propTypes = {
  product: PropTypes.object,
};

export default ProductDetails;
